/* eslint-disable @typescript-eslint/no-explicit-any */
import { useId, useState, useRef, useEffect, useCallback } from "react";
import Select, { SelectProps, SelectState } from "react-dropdown-select";

import {
  SelectOptionType,
  SelectOptionAdditionalItemType,
  TooltipType,
} from "./types";

import { Checkbox, IconSearch } from "tvs-unified-lib/dist";
import { MultipleText } from "../styles";

type FormSelectType = {
  value: SelectOptionType[];
  onChange: (e: SelectOptionType[]) => void;
  options: SelectOptionType[];
  isMultiple?: boolean;
  loading?: boolean;
  keepOpen?: boolean;
  defaultValue?: string | number;
  containerClassName?: string;
  placeholder?: string;
  className?: string;
  formLabel?: string;
  formSubLabel?: string;
  inputHelpBlock?: string;
  isRequired?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  withSearch?: boolean;
  withCheckbox?: boolean;
  onSearch?: (value: string) => void;
  create?: boolean;
  disabled?: boolean;
  tooltip?: TooltipType;
  itemRenderer?: (item: SelectOptionType) => JSX.Element;
  additionalItems?: SelectOptionAdditionalItemType[];
  additionalSearchFilterRenderer?: () => JSX.Element;
  fetchOptions?: (
    searchTerm: string,
    page: number
  ) => Promise<{ data: SelectOptionType[]; totalPages: number }>;
  totalPages?: number;
  dropdownPosition?: "top" | "bottom" | "auto";
  dropdownHeight?: string;
};

interface CustomRendererArgs<T = SelectOptionType> {
  props: SelectProps<T>;
  state: SelectState<T>;
  methods: any;
}

const FormSelect = (props: FormSelectType) => {
  const {
    value,
    onChange,
    options,
    isMultiple = false,
    loading = false,
    keepOpen = false,
    placeholder = "Select...",
    className = "",
    containerClassName,
    formLabel,
    formSubLabel,
    inputHelpBlock,
    isRequired = false,
    hasError = false,
    errorMessage = "",
    withSearch = true,
    withCheckbox = false,
    create = false,
    disabled = false,
    itemRenderer,
    additionalItems,
    additionalSearchFilterRenderer,
    fetchOptions,
    totalPages: totalPagesProp,
    dropdownPosition = "auto",
    dropdownHeight = "300px",
  } = props;

  const componentId = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFetchMore = useCallback(
    async (searchTerm: string) => {
      if (
        fetchOptions &&
        totalPagesProp &&
        currentPage < totalPagesProp &&
        !isFetchingMore
      ) {
        setIsFetchingMore(true);
        try {
          const response = await fetchOptions(searchTerm, currentPage + 1);
          if (response.data && response.data.length > 0) {
            setCurrentPage((prevPage) => prevPage + 1);
          } else if (currentPage + 1 >= response.totalPages) {
            setCurrentPage(response.totalPages);
          }
        } catch (error) {
          console.error("Error fetching more options:", error);
        } finally {
          setIsFetchingMore(false);
        }
      }
    },
    [fetchOptions, totalPagesProp, currentPage, isFetchingMore]
  );

  const handleScroll = useCallback(() => {
    if (
      dropdownRef.current &&
      fetchOptions &&
      totalPagesProp &&
      currentPage < totalPagesProp &&
      !isFetchingMore
    ) {
      const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        handleFetchMore(
          dropdownRef.current.querySelector<HTMLInputElement>(
            ".react-dropdown-select-search"
          )?.value || ""
        );
      }
    }
  }, [
    fetchOptions,
    totalPagesProp,
    currentPage,
    isFetchingMore,
    handleFetchMore,
  ]);

  useEffect(() => {
    if (dropdownRef.current && fetchOptions) {
      dropdownRef.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (dropdownRef.current) {
        dropdownRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll, fetchOptions]);

  const customDropdownRenderer = ({
    props: selectProps,
    methods,
  }: CustomRendererArgs) => {
    if (!itemRenderer) return;
    
    return (
      <div style={{ maxHeight: dropdownHeight, overflowY: "auto" }}>
        {selectProps.options.map((option: SelectOptionType) => {
          const isSelected: boolean = !!props.value.find(
            (v) => v.value === option.value
          );

          const handleOptionClick = () => {
            if (option.disabled) return;
            methods.addItem(option);
          };

          return (
            <span
              key={option.value}
              style={{ cursor: option.disabled ? "not-allowed" : "pointer" }}
              onClick={handleOptionClick}
              className={`react-dropdown-select-item ${
                option.disabled ? "react-dropdown-select-item-disabled" : ""
              } ${isSelected ? "react-dropdown-select-item-selected" : ""}`}
            >
              {itemRenderer!(option)}
            </span>
          );
        })}
      </div>
    );
  };

  const customDropdownRendererMutiple = ({
    props: selectProps,
    state,
    methods,
  }: CustomRendererArgs) => {
    const regexp = new RegExp(state.search, "i");

    const actualOptions = selectProps.options.filter(
      (o: SelectOptionType) => o.value !== "all"
    );
    const actualOptionsCount = actualOptions.length;
    const selectedCount = props.value.length;

    const isAllSelected =
      actualOptionsCount > 0 && selectedCount === actualOptionsCount;

    return (
      <div
        ref={dropdownRef}
        style={{ maxHeight: dropdownHeight, overflowY: "auto" }}
      >
        {(withSearch === true ||
          additionalSearchFilterRenderer !== undefined) && (
          <div className="theme-select-header-filter">
            {withSearch && (
              <div className="theme-select-search-wrapper">
                <input
                  type="text"
                  value={state.search}
                  onChange={methods.setSearch}
                  className="theme-form-control"
                  placeholder="Search"
                  autoComplete="off"
                  autoFocus={true}
                />
                <IconSearch className="theme-select-search-icon" />
              </div>
            )}
            {additionalSearchFilterRenderer && additionalSearchFilterRenderer()}
          </div>
        )}
        {selectProps.options
          .filter((item) =>
            regexp.test(
              item[selectProps.searchBy] || item.label?.toString() || ""
            )
          )
          .map((option: SelectOptionType) => {
            const isSelected: boolean = !!props.value.find(
              (v) => v.value === option.value
            );

            const handleSelectAllChange = () => {
              if (option.disabled) return;
              if (isAllSelected) {
                methods.clearAll();
                onChange([]);
              } else {
                methods.selectAll(actualOptions);
                onChange(actualOptions);
              }
            };

            const handleOptionChange = () => {
              if (option.disabled) return;
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              isSelected
                ? methods.removeItem(null, option, !keepOpen)
                : methods.addItem(option);
            };

            if (option.value === "all") {
              if (!isMultiple) return null;

              return (
                <div
                  key={option.value}
                  style={{ cursor: "unset" }}
                  onClick={!withCheckbox ? handleSelectAllChange : undefined}
                  className={`react-dropdown-select-item react-dropdown-select-item-select-all ${
                    option.disabled ? "react-dropdown-select-item-disabled" : ""
                  } ${
                    isAllSelected ? "react-dropdown-select-item-selected" : ""
                  }`}
                >
                  {withCheckbox && (
                    <Checkbox
                      name={`${componentId}-cb-all`}
                      value={isAllSelected ? "all" : ""}
                      checkboxValues={[{ label: "", value: "all" }]}
                      onInputChange={handleSelectAllChange}
                    />
                  )}
                  <span
                    style={{
                      marginLeft: withCheckbox && isMultiple ? "8px" : "0",
                    }}
                  >
                    {option.other?.label || option.label || "Select All"}
                  </span>
                </div>
              );
            }

            return (
              <div
                key={option.value}
                style={{ cursor: "unset" }}
                onClick={
                  !withCheckbox || !isMultiple ? handleOptionChange : undefined
                }
                className={`react-dropdown-select-item ${
                  option.disabled ? "react-dropdown-select-item-disabled" : ""
                } ${isSelected ? "react-dropdown-select-item-selected" : ""}`}
              >
                {withCheckbox && isMultiple && (
                  <Checkbox
                    name={`${componentId}-cb-${option.value}`}
                    value={isSelected ? option.value.toString() : ""}
                    checkboxValues={[
                      {
                        label: "",
                        value: option.value.toString(),
                      },
                    ]}
                    onInputChange={handleOptionChange}
                  />
                )}
                <span
                  style={{
                    marginLeft: withCheckbox && isMultiple ? "8px" : "0",
                  }}
                >
                  {option.label}
                </span>
              </div>
            );
          })}
        {isFetchingMore && <div>Loading more options...</div>}
        {additionalItems &&
          additionalItems.map(
            (item: SelectOptionAdditionalItemType, index: number) => {
              return (
                <span
                  key={`${componentId}-added-option-${index}`}
                  className="react-dropdown-select-item"
                  onClick={() => item.onClick && item.onClick()}
                >
                  {item.label()}
                </span>
              );
            }
          )}
      </div>
    );
  };

  const customContentRenderer = () => {
    const {
      value: selectedValues,
      options: componentOptions,
      isMultiple,
    } = props;
    const count = selectedValues.length;

    if (!count) {
      return;
    }

    if (!isMultiple) {
      return <span>{selectedValues[0]?.label}</span>;
    }

    const allOptionDefinition = componentOptions.find(
      (opt) => opt.value === "all"
    );

    const actualOptions = componentOptions.filter((opt) => opt.value !== "all");

    const allActualOptionsSelected =
      actualOptions.length > 0 && count === actualOptions.length;

    if (allActualOptionsSelected && allOptionDefinition) {
      const allSelectedLabel =
        allOptionDefinition.other?.label ||
        allOptionDefinition.label ||
        "All items selected";
      return <span>{allSelectedLabel}</span>;
    } else {
      return count > 3 ? (
        <span>{count} selected</span>
      ) : (
        <MultipleText>
          {selectedValues.map((item) => item?.label ?? "").join(", ")}
        </MultipleText>
      );
    }
  };

  return (
    <div
      className={`${withSearch ? "theme-select-custom-search" : ""} ${
        containerClassName ?? ""
      }`}
    >
      {formLabel && (
        <label className="theme-form-input-label">
          {formLabel} {isRequired && <span>*</span>}{" "}
        </label>
      )}
      {formSubLabel && (
        <label className="theme-form-sublabel">{formSubLabel}</label>
      )}
      <div className="theme-select-input-wrapper">
        {placeholder && value.length <= 0 && !loading && (
          <span className="theme-select-placeholder">{placeholder}</span>
        )}
        <Select
          options={options}
          className={`theme-select-container ${
            withCheckbox ? "theme-select-checkbox" : ""
          } ${hasError ? "theme-form-control-has-error" : ""} ${className}`}
          values={value}
          onChange={onChange}
          placeholder={placeholder}
          multi={isMultiple}
          loading={loading}
          disabled={disabled}
          create={create}
          keepOpen={keepOpen}
          searchable={withSearch}
          dropdownPosition={dropdownPosition}
          dropdownHeight={dropdownHeight}
          dropdownRenderer={
            isMultiple
              ? customDropdownRendererMutiple
              : itemRenderer
              ? customDropdownRenderer
              : undefined
          }
          contentRenderer={customContentRenderer}
          searchBy="label"
          backspaceDelete={false}
        />
      </div>
      {inputHelpBlock && (
        <span className="theme-form-helpblock">{inputHelpBlock}</span>
      )}
      {hasError && (
        <div className="theme-font-sm color-theme-red-500 theme-form-error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default FormSelect;
